import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STAGES = ['new', 'contacted', 'scheduled', 'running', 'closed'];
const label = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const usDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-');

function SignupRow({ s, onStage }) {
  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm" data-testid={`pilot-signup-${s.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div><span className="font-semibold text-white">{s.website}</span> <span className="text-gray-400">· {s.name}, {s.role}</span></div>
        <select value={s.stage} onChange={(e) => onStage(s.id, e.target.value)} className="rounded-full border border-white/15 bg-[#0a0a0a] px-3 py-1 text-xs text-gray-200" data-testid={`pilot-signup-${s.id}-stage`}>
          {STAGES.map((st) => <option key={st} value={st}>{label(st)}</option>)}
        </select>
      </div>
      <p className="text-gray-400">{s.channel} · {s.monthly_cancellations} cancellations a month · start {s.start_month.split('-').reverse().join('-')} · received {usDate(s.created_at)}{s.emailed ? '' : ' · email not sent'}</p>
      {s.note && <p className="mt-2 text-gray-300 whitespace-pre-wrap">{s.note}</p>}
      <a href={`mailto:${s.email}?subject=${encodeURIComponent('Your 30-day cancellation pilot')}`} className="mt-2 inline-block text-xs text-amber-300 underline" data-testid={`pilot-signup-${s.id}-reply`}>Reply to {s.email}</a>
    </li>
  );
}

export function PilotSignupsInbox() {
  const { token } = useAuth();
  const [data, setData] = useState({ signups: [], counts: {}, total: 0 });
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/pilot-signups`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      setError('Could not load pilot signups. Refresh to try again.');
    }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const onStage = async (id, stage) => {
    setError('');
    const res = await fetch(`${API}/api/admin/pilot-signups/${id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ stage }) }).catch(() => null);
    if (!res?.ok) setError('Could not save that stage. Please try again.');
    load();
  };
  const list = data.signups.filter((s) => filter === 'all' || s.stage === filter);
  return (
    <div className="space-y-4" data-testid="pilot-signups-inbox">
      <div className="flex flex-wrap gap-2" data-testid="pilot-signups-filters">
        {['all', ...STAGES].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-xs ${filter === f ? 'border-amber-400/60 text-white' : 'border-white/15 text-gray-400 hover:text-white'}`} data-testid={`pilot-signups-filter-${f}`}>
            {f === 'all' ? `All (${data.total})` : `${label(f)} (${data.counts[f] || 0})`}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-300" data-testid="pilot-signups-error">{error}</p>}
      {loading && <p className="text-sm text-gray-500" data-testid="pilot-signups-loading">Loading...</p>}
      <ul className="space-y-3" data-testid="pilot-signups-list">
        {list.map((s) => <SignupRow key={s.id} s={s} onStage={onStage} />)}
        {!loading && !list.length && <li className="text-sm text-gray-500" data-testid="pilot-signups-empty">No pilot signups here yet.</li>}
      </ul>
    </div>
  );
}
